import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_PUBLIC_SITE,
  GET_TINYCMS_DELETED_PAGES,
  GET_TINYCMS_PAGES,
  GET_TINYCMS_SITE,
} from '../../graphql';
import { RESTORE_TINYCMS_PAGE } from '../../graphql/mutations';
import { homeGap, restoreOrder, type GapDeleted, type GapPage, type HomeGap } from './homeGap';

/**
 * Whether the live site is showing readers the bare article list because none
 * of its pages are live (see homeGap). The same queries, with the same
 * variables, as Content → Pages, so the two share Apollo's cache.
 */
export function useSiteHomeGap(accountId: string | null | undefined): {
  gap: HomeGap | null;
  deleted: GapDeleted[];
} {
  const skip = !accountId;
  const { data: publicData } = useQuery(GET_PUBLIC_SITE, { variables: { accountId }, skip });
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const { data: deletedData } = useQuery(GET_TINYCMS_DELETED_PAGES, {
    variables: { accountId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const pages: GapPage[] = pagesData?.tinycmsPages || [];
  const deleted: GapDeleted[] = deletedData?.tinycmsDeletedPages || [];
  const publicEnabled = publicData?.account?.publicEnabled;
  const candidate = Boolean(pagesData && deletedData) && homeGap({ publicEnabled, pages, deleted });
  // The site document is only needed to say whether the menu is gone too, and
  // which page is the home page (a new draft there changes the fix).
  const { data: siteData } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: skip || !candidate,
    fetchPolicy: 'cache-and-network',
  });
  if (!candidate) return { gap: null, deleted };
  const working = siteData?.tinycmsSite?.working;
  return {
    gap: homeGap({
      publicEnabled,
      pages,
      deleted,
      navigation: working?.navigation ?? null,
      homeSlug: working?.layout?.homePage ?? null,
    }),
    deleted,
  };
}

/** A one-line amber warning, with an optional link to the fix (or a click, on the same screen). */
export function SiteHomeNotice({
  text,
  action,
}: {
  text: string;
  action?: { label: string; to?: string; onClick?: () => void } | null;
}) {
  return (
    <div className="ink-notice" role="status">
      <span className="ink-notice-dot" aria-hidden="true" />
      <span className="ink-notice-text">{text}</span>
      {action?.onClick ? (
        <button type="button" className="ink-notice-action" onClick={action.onClick}>
          {action.label}
        </button>
      ) : action?.to ? (
        <Link to={action.to} className="ink-notice-action">
          {action.label}
        </Link>
      ) : null}
    </div>
  );
}

/**
 * Restore every deleted page whose address is free, parents first, one at a
 * time. A page the server refuses doesn't stop the rest.
 */
export function useRestoreAllPages(accountId: string | null | undefined) {
  const [restore] = useMutation(RESTORE_TINYCMS_PAGE);
  const [busy, setBusy] = useState(false);
  const run = async (items: GapDeleted[]) => {
    const restored: string[] = [];
    const failed: Array<{ title: string; message: string }> = [];
    setBusy(true);
    try {
      for (const d of restoreOrder(items)) {
        try {
          await restore({ variables: { accountId, id: d.id } });
          restored.push(d.id);
        } catch (err) {
          failed.push({
            title: d.title || 'Untitled',
            message: err instanceof Error ? err.message : 'Couldn’t restore it',
          });
        }
      }
    } finally {
      setBusy(false);
    }
    return { restored, failed };
  };
  return { run, busy };
}
